<template>
  <div class="firmware-launch pa-6 pa-md-10">
    <div class="launch-column">
      <div class="d-flex align-center flex-wrap mb-4" style="gap: 12px">
        <v-btn small text @click="$router.back()"><v-icon small left>mdi-arrow-left</v-icon>{{ $t('firmware.launch.back') }}</v-btn>
        <div class="text-h6">{{ $tc('firmware.launch.title', chargingPointIds.length, {count: chargingPointIds.length}) }}</div>
      </div>

      <div v-if="loading && !plan" class="text-center my-10"><v-progress-circular indeterminate color="primary" /></div>

      <template v-else-if="plan">
        <!-- 1. Les bornes -->
        <v-card outlined class="mb-4">
          <v-card-title class="text-subtitle-1">1. {{ $t('firmware.launch.charging-points') }}</v-card-title>
          <v-card-text>
            <div v-for="group in plan.models" :key="group.vendor + group.model" class="d-flex justify-space-between mb-1" style="gap: 16px">
              <span style="min-width: 0">{{ group.vendor }} · <span class="mono">{{ group.model }}</span> — <span class="mono">{{ versionList(group.versions) }}</span></span>
              <b class="text-no-wrap">{{ $tc('firmware.launch.count', group.count, {count: group.count}) }}</b>
            </div>
          </v-card-text>
        </v-card>

        <!-- 2. La version visée -->
        <v-card outlined class="mb-4">
          <v-card-title class="text-subtitle-1">2. {{ $t('firmware.launch.target') }}</v-card-title>
          <v-card-text>
            <v-alert v-if="!plan.options.length" type="warning" text dense>{{ $t('firmware.launch.no-option') }}</v-alert>
            <template v-else>
              <!-- Géré par le constructeur : on ne choisit pas la version, la borne prend ce qu'il lui propose -->
              <v-alert v-if="target && target.managed" type="info" text dense>{{ $t('firmware.launch.managed', {version: target.version}) }}</v-alert>
              <v-select
                  v-else
                  v-model="versionId"
                  :items="plan.options"
                  item-value="id"
                  item-text="version"
                  :label="$t('firmware.launch.target-label')"
                  outlined
                  dense
                  @change="load"
              >
                <template #item="{item}">
                  <span class="mono">{{ item.version }}</span>
                  <v-chip x-small class="ml-2" :color="firmwareStatusColor(item.status)" text-color="white">{{ $t('firmware.firmware-statuses.' + item.status) }}</v-chip>
                </template>
                <template #selection="{item}">
                  <span class="mono">{{ item.version }}</span>
                  <v-chip x-small class="ml-2" :color="firmwareStatusColor(item.status)" text-color="white">{{ $t('firmware.firmware-statuses.' + item.status) }}</v-chip>
                </template>
              </v-select>

              <template v-if="plan.plan">
                <v-sheet color="grey lighten-4" rounded class="pa-3 mb-3">
                  <div class="font-weight-medium mb-2">{{ $t('firmware.launch.paths') }}</div>
                  <div v-for="(path, index) in plan.plan.paths" :key="index" class="d-flex align-center flex-wrap mb-1" style="gap: 8px">
                    <b style="min-width: 80px">{{ $tc('firmware.launch.count', path.count, {count: path.count}) }}</b>
                    <span class="mono">{{ versionList(path.from) }}</span>
                    <template v-for="(step, i) in path.steps">
                      <v-icon :key="'a' + i" small>mdi-arrow-right</v-icon>
                      <v-chip :key="'s' + i" small :outlined="i < path.steps.length - 1" :color="i === path.steps.length - 1 ? 'primary' : undefined" class="mono">{{ step }}</v-chip>
                    </template>
                    <span v-if="path.steps.length === 1" class="text-caption text--secondary">{{ $t('firmware.launch.direct') }}</span>
                  </div>
                  <div class="text-caption text--secondary mt-2">{{ $t('firmware.launch.paths-hint') }}</div>
                </v-sheet>
                <div v-for="excluded in plan.plan.excluded" :key="excluded.reason" class="text-body-2 text--secondary">
                  <v-tooltip bottom>
                    <template #activator="{on}"><span v-on="on"><b>{{ $tc('firmware.launch.count', excluded.count, {count: excluded.count}) }}</b> {{ $t('firmware.launch.set-aside') }} : {{ excluded.reason }}</span></template>
                    {{ excluded.chargingPoints.join(', ') }}
                  </v-tooltip>
                </div>
                <template v-if="plan.plan.forced">
                  <v-alert type="warning" text dense class="mt-3">{{ $t('firmware.launch.forced') }}</v-alert>
                  <v-textarea v-model="form.reason" :label="$t('firmware.launch.reason')" rows="2" auto-grow outlined dense />
                </template>
              </template>
            </template>
          </v-card-text>
        </v-card>

        <!-- 3. Le rythme : sans objet pour une seule borne -->
        <v-card v-if="!single" outlined class="mb-4">
          <v-card-title class="text-subtitle-1">3. {{ $t('firmware.launch.pace') }}</v-card-title>
          <v-card-text>
            <v-radio-group v-model="form.pace" class="mt-0" hide-details>
              <v-row dense>
                <v-col v-for="pace in paces" :key="pace" cols="12" md="4">
                  <v-sheet outlined rounded class="pa-3 fill-height" :class="{'pace--active': form.pace === pace}">
                    <v-radio :value="pace">
                      <template #label>
                        <div>
                          <div class="font-weight-medium text--primary">{{ $t('firmware.launch.paces.' + pace) }}</div>
                          <div class="text-caption">{{ $t('firmware.launch.paces-hint.' + pace) }}</div>
                        </div>
                      </template>
                    </v-radio>
                  </v-sheet>
                </v-col>
              </v-row>
            </v-radio-group>
            <div class="font-weight-medium mt-4">{{ $t('firmware.launch.on-failure') }}</div>
            <v-radio-group v-model="form.failureStopsSite" class="mt-1" hide-details>
              <v-radio :value="true" :label="$t('firmware.launch.failure-stops-site')" />
              <v-radio :value="false" :label="$t('firmware.launch.failure-continues')" />
            </v-radio-group>
            <div class="d-flex align-center flex-wrap mt-3" style="gap: 8px">
              <v-checkbox v-model="form.pauseEnabled" :label="$t('firmware.launch.pause-after')" hide-details class="mt-0" />
              <v-text-field v-model.number="form.pauseAfterFailures" :disabled="!form.pauseEnabled" type="number" min="1" outlined dense hide-details style="max-width: 90px" />
              <span>{{ $t('firmware.launch.failures') }}</span>
            </div>
          </v-card-text>
        </v-card>

        <!-- 4. Quand -->
        <v-card outlined class="mb-4">
          <v-card-title class="text-subtitle-1">{{ single ? 3 : 4 }}. {{ $t('firmware.launch.when') }}</v-card-title>
          <v-card-text>
            <v-radio-group v-model="form.when" class="mt-0" hide-details>
              <v-radio value="now" :label="$t('firmware.launch.now')" />
              <v-radio value="window" :label="$t('firmware.launch.window')" />
            </v-radio-group>
            <div v-if="form.when === 'window'" class="d-flex align-center flex-wrap mt-3 ml-8" style="gap: 8px">
              <span>{{ $t('firmware.launch.every-day-from') }}</span>
              <v-text-field v-model="form.windowStart" type="time" outlined dense hide-details style="max-width: 130px" />
              <span>{{ $t('firmware.launch.to') }}</span>
              <v-text-field v-model="form.windowEnd" type="time" outlined dense hide-details style="max-width: 130px" />
              <span>{{ $t('firmware.launch.starting') }}</span>
              <v-text-field v-model="form.startDate" type="date" outlined dense hide-details style="max-width: 170px" />
            </div>
            <div v-if="form.when === 'window'" class="text-caption text--secondary mt-2 ml-8">{{ $t('firmware.launch.window-hint') }}</div>
          </v-card-text>
        </v-card>

        <v-card outlined>
          <v-card-text class="d-flex align-center flex-wrap" style="gap: 16px">
            <div class="flex-grow-1">{{ summary }}</div>
            <v-btn color="primary" :loading="sending" :disabled="!launchable" @click="launch">
              {{ form.when === 'window' ? $t('firmware.launch.schedule') : $t('firmware.launch.start') }}
            </v-btn>
          </v-card-text>
        </v-card>
      </template>
    </div>
  </div>
</template>

<script>
import FirmwareRepository from "@repository/FirmwareRepository";
import FirmwareMixin from "@pages/Firmware/FirmwareMixin";

const PACES = ['one-per-site', 'one-at-a-time', 'all-at-once']

/**
 * Mettre à jour des bornes choisies, en quatre blocs : les bornes, la version visée (et le chemin de chacune), le rythme
 * (et que faire si une borne échoue), quand (tout de suite, ou dans une fenêtre de maintenance à l'heure du site).
 */
export default {
  mixins: [FirmwareMixin],

  props: {
    // Les bornes d'un chiffre de la fiche d'un constructeur, ou une borne seule ; absentes après un rechargement de la page.
    chargingPointIds: {type: Array, default: () => []},
    // Le firmware visé, s'il est choisi d'avance (« Mettre à jour vers ce firmware »).
    targetVersionId: {type: String, default: null},
  },

  data() {
    const today = new Date()
    return {
      plan: null,
      loading: false,
      // La page d'où l'on vient (fiche d'une borne dans son organisation, fiche constructeur…) : on y revient après le lancement.
      returnTo: null,
      sending: false,
      versionId: this.targetVersionId,
      paces: PACES,
      form: {
        reason: '',
        pace: 'one-per-site',
        failureStopsSite: true,
        pauseEnabled: true,
        pauseAfterFailures: 3,
        // Une borne seule part tout de suite ; plusieurs, la nuit.
        when: this.chargingPointIds.length === 1 ? 'now' : 'window',
        windowStart: '21:00',
        windowEnd: '06:00',
        startDate: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`,
      },
    }
  },

  computed: {
    target() {
      return (this.plan?.options || []).find(option => option.id === this.versionId) || null
    },
    single() {
      return this.chargingPointIds.length === 1
    },
    launchable() {
      const plan = this.plan?.plan
      return !!plan && plan.toUpdate > 0 && (!plan.forced || !!this.form.reason.trim())
        && (!this.form.pauseEnabled || this.form.pauseAfterFailures >= 1)
        && (this.form.when === 'now' || (!!this.form.windowStart && !!this.form.windowEnd && this.form.windowStart !== this.form.windowEnd))
    },
    summary() {
      const plan = this.plan?.plan
      if (!plan) {
        return ''
      }
      const when = this.form.when === 'now'
        ? this.$t('firmware.launch.summary-now')
        : this.$t('firmware.launch.summary-window', {start: this.form.windowStart, end: this.form.windowEnd})
      if (this.single) {
        return this.target && this.target.managed
          ? this.$t('firmware.launch.summary-managed', {when})
          : this.$t('firmware.launch.summary-single', {version: plan.version, when})
      }
      return this.$t('firmware.launch.summary', {
        count: plan.toUpdate,
        sites: plan.sites,
        version: plan.version,
        pace: this.$t('firmware.launch.paces.' + this.form.pace).toLowerCase(),
        when,
      })
    },
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      if (from.name && !['firmware', 'firmware.launch'].includes(from.name)) {
        vm.returnTo = from.fullPath
      }
    })
  },

  mounted() {
    // Après un rechargement, la sélection (passée en mémoire) est perdue : on le dit, plutôt que de repartir en silence.
    if (!this.chargingPointIds.length) {
      this.$dialog.notify.warning(this.$t('firmware.launch.selection-lost'))
      this.$router.replace({name: 'firmware'})
      return
    }
    this.load()
  },

  methods: {
    // Les versions de départ : une borne qui n'en annonce pas est dite comme telle, et une longue liste se résume.
    versionList(versions) {
      const names = versions.map(version => version || this.$t('firmware.launch.no-version'))
      if (names.length <= 6) {
        return names.join(' · ') || '-'
      }
      return names.slice(0, 6).join(' · ') + ' · ' + this.$t('firmware.launch.more', {count: names.length - 6})
    },

    load() {
      this.loading = true
      FirmwareRepository.plan(this.chargingPointIds, this.versionId).then(plan => {
        this.plan = plan
        this.versionId = plan.plan?.versionId || null
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.loading = false
      })
    },

    launch() {
      this.sending = true
      const windowed = this.form.when === 'window'
      // Le départ : le jour choisi à l'heure d'ouverture de la fenêtre (heure du navigateur, envoyée avec son fuseau).
      const startAt = windowed ? new Date(`${this.form.startDate}T${this.form.windowStart}`).toISOString() : null
      FirmwareRepository.startCampaign({
        chargingPointIds: this.chargingPointIds,
        versionId: this.versionId,
        pace: this.form.pace,
        failureStopsSite: this.form.failureStopsSite,
        pauseAfterFailures: this.form.pauseEnabled ? this.form.pauseAfterFailures : null,
        startAt,
        windowStart: windowed ? this.form.windowStart : null,
        windowEnd: windowed ? this.form.windowEnd : null,
        reason: this.plan.plan.forced ? this.form.reason.trim() : null,
      }).then(({id}) => {
        // On reste dans son contexte (l'organisation, le constructeur) ; la mise à jour se suit aussi dans Firmware › Mises à jour.
        if (this.returnTo) {
          this.notifySuccess(windowed ? 'firmware.launch.scheduled-back' : 'firmware.launch.started-back')
          this.$router.push(this.returnTo)
          return
        }
        this.notifySuccess(windowed ? 'firmware.launch.scheduled' : 'firmware.launch.started')
        this.$router.push({name: 'firmware', query: {tab: 'updates', campaign: id}})
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.sending = false
      })
    },
  },
}
</script>

<style scoped lang="scss">
.firmware-launch {
  .launch-column {
    max-width: 920px;
    margin: 0 auto;
  }
  .mono {
    font-family: ui-monospace, monospace;
  }
  .pace--active {
    border: 2px solid var(--v-primary-base) !important;
  }
}
</style>
